import { Subject, Observable } from 'rxjs';

/**
 * Dynamic Dialog Reference - Razor UI Design System
 * Drop-in replacement for legacy DynamicDialogRef
 *
 * Provides a reference to an opened dynamic dialog for:
 * - Closing the dialog programmatically
 * - Passing data back when closing
 * - Subscribing to close events
 */
export class DynamicDialogRef<T = any> {
  private closed = false;
  private _onClose = new Subject<T | undefined>();
  private _onDestroy = new Subject<void>();

  /** Observable that emits when the dialog is closed */
  readonly onClose: Observable<T | undefined> = this._onClose.asObservable();

  /** Observable that emits when the dialog is destroyed */
  readonly onDestroy: Observable<void> = this._onDestroy.asObservable();

  /**
   * Close the dialog and optionally pass data back
   * @param data Optional data to pass back to the opener
   */
  close(data?: T): void {
    if (this.closed) return;
    this.closed = true;
    this._onClose.next(data);
    this._onClose.complete();
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  /**
   * Destroy the dialog (called internally)
   */
  destroy(): void {
    if (this.closed) return;
    this.closed = true;
    this._onDestroy.next();
    this._onDestroy.complete();
    this._onClose.complete();
  }
}
